/**
 * ACTIVITY-CORE — textos humanos del "feed del hogar" (i18n).
 *
 * Parte ESPECÍFICA de la app: el mapa entity_type+action → frase cálida. La
 * estructura (patch, agrupación, offline) vive en @mealplan/shared. Se devuelven
 * descriptores `msg` (no strings) para que el idioma sea reactivo.
 *
 * NOMBRES: el patch solo trae nombre fiable en snapshots created/deleted; un
 * toggle `checked` es solo `{checked}`. Por eso `activitySentence` acepta un
 * RESOLVER que resuelve el nombre desde las tablas LOCALES por (type, id):
 * shopping_item.name, recipe.name, pantry_item.name; y para meal_plan_entry el
 * nombre de la receta vía entry.recipe_id → recipe.name. Cae al nombre del
 * patch y, si no hay, a una frase anónima cálida. Nunca lanza.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { type ActivityRow, parseActivityPatch } from "@mealplan/shared";

/** Resuelve el nombre visible de una entidad desde el SQLite local. */
export type EntityNameResolver = (
  entityType: string | null,
  entityId: string | null,
) => string | null;

interface ActivitySentence {
  /** Frase con `{name}` interpolado. */
  named: MessageDescriptor;
  /** Frase sin nombre (el patch no lo trae y no se pudo resolver). */
  anonymous: MessageDescriptor;
}

/**
 * Clave semántica fina: refina `updated`/`deleted` con lo que de verdad pasó
 * (marcar comprado, mover a papelera…) mirando el patch.
 */
export function activitySemanticKey(row: ActivityRow): string {
  const action = row.action ?? "updated";
  const patch = parseActivityPatch(row.patch);
  if (row.entity_type === "shopping_item" && action === "updated" && patch.checked) {
    return patch.checked.new ? "checked" : "unchecked";
  }
  if (action === "deleted") {
    // Solo la receta tiene papelera: transición de deleted_at = a la papelera;
    // snapshot completo (sin deleted_at) = purga definitiva. El resto de
    // entidades (compra/planner/despensa) se borran duro → "deleted".
    if (row.entity_type === "recipe") {
      return patch.deleted_at ? "trashed" : "purged";
    }
    return "deleted";
  }
  return action;
}

const SHOPPING_ITEM_TEXTS: Record<string, ActivitySentence> = {
  checked: {
    named: msg`marked {name} as bought`,
    anonymous: msg`marked an item as bought`,
  },
  unchecked: { named: msg`unmarked {name}`, anonymous: msg`unmarked an item` },
  created: { named: msg`added {name} to the list`, anonymous: msg`added an item to the list` },
  deleted: {
    named: msg`removed {name} from the list`,
    anonymous: msg`removed an item from the list`,
  },
  updated: { named: msg`edited the item {name}`, anonymous: msg`edited an item` },
};

const MEAL_PLAN_ENTRY_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`planned {name}`, anonymous: msg`planned a meal` },
  deleted: {
    named: msg`removed {name} from the planner`,
    anonymous: msg`removed a meal from the planner`,
  },
  updated: {
    named: msg`updated {name} in the planner`,
    anonymous: msg`changed a meal in the planner`,
  },
};

const RECIPE_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`added the recipe {name}`, anonymous: msg`added a recipe` },
  updated: { named: msg`edited the recipe {name}`, anonymous: msg`edited a recipe` },
  trashed: { named: msg`moved {name} to the trash`, anonymous: msg`moved a recipe to the trash` },
  purged: {
    named: msg`permanently deleted {name}`,
    anonymous: msg`permanently deleted a recipe`,
  },
  restored: { named: msg`restored {name}`, anonymous: msg`restored a recipe` },
  reverted: { named: msg`undid a change on {name}`, anonymous: msg`undid a change` },
};

const PANTRY_ITEM_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`added {name} to the pantry`, anonymous: msg`added an item to the pantry` },
  deleted: {
    named: msg`removed {name} from the pantry`,
    anonymous: msg`removed an item from the pantry`,
  },
  updated: { named: msg`updated {name} in the pantry`, anonymous: msg`updated a pantry item` },
};

const FALLBACK: ActivitySentence = {
  named: msg`changed {name}`,
  anonymous: msg`made a change`,
};

const TABLES: Record<string, Record<string, ActivitySentence>> = {
  shopping_item: SHOPPING_ITEM_TEXTS,
  meal_plan_entry: MEAL_PLAN_ENTRY_TEXTS,
  recipe: RECIPE_TEXTS,
  pantry_item: PANTRY_ITEM_TEXTS,
};

/** Nombre visible extraído SOLO del patch (name/text, new u old). */
function nameFromPatch(row: ActivityRow): string | null {
  const patch = parseActivityPatch(row.patch);
  for (const key of ["name", "text"] as const) {
    const change = patch[key];
    if (!change) {
      continue;
    }
    const value = change.new ?? change.old;
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

/**
 * Nombre visible de la entidad: primero el resolver (tablas locales), luego el
 * patch. `null` si no se pudo resolver (se usará la frase anónima).
 */
export function activityEntityName(row: ActivityRow, resolve?: EntityNameResolver): string | null {
  const resolved = resolve?.(row.entity_type, row.entity_id);
  if (resolved && resolved.trim().length > 0) {
    return resolved;
  }
  return nameFromPatch(row);
}

/**
 * Frase i18n de una entrada: descriptor + valores. Renderizar con
 * `t(descriptor, values)` en el componente.
 */
export function activitySentence(
  row: ActivityRow,
  resolve?: EntityNameResolver,
): { descriptor: MessageDescriptor; values: Record<string, string> } {
  const key = activitySemanticKey(row);
  const table = row.entity_type ? (TABLES[row.entity_type] ?? {}) : {};
  const sentence = table[key] ?? FALLBACK;
  const name = activityEntityName(row, resolve);
  if (name) {
    return { descriptor: sentence.named, values: { name } };
  }
  return { descriptor: sentence.anonymous, values: {} };
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** "hace 5 min" localizado con Intl; cae al día/fecha para lo antiguo. */
export function formatRelativeTime(
  iso: string | null,
  locale: string,
  now: Date = new Date(),
): string {
  if (!iso) {
    return "";
  }
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    return "";
  }
  const diff = ts - now.getTime();
  const abs = Math.abs(diff);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (abs < MINUTE_MS) {
      return rtf.format(Math.round(diff / SECOND_MS), "second");
    }
    if (abs < HOUR_MS) {
      return rtf.format(Math.round(diff / MINUTE_MS), "minute");
    }
    if (abs < DAY_MS) {
      return rtf.format(Math.round(diff / HOUR_MS), "hour");
    }
    if (abs < 7 * DAY_MS) {
      return rtf.format(Math.round(diff / DAY_MS), "day");
    }
    return new Date(ts).toLocaleDateString(locale);
  } catch {
    return new Date(ts).toLocaleString();
  }
}
